import { useTranslation } from '@framework/i18n';
import { AnyObject } from '@framework/validation';
import { Button } from 'antd';
import { isEqual, omit } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { TableSearchCollapse } from '../collapse/TableSearchCollapse';
import { AdvancedSearch, AdvancedSearchProps } from './AdvancedSearch';
import { AdminMessageFilteredList } from './message/AdminMessageFilteredList';

export interface IAdvancedSearchCollapseFormProps<
  TSearchable extends AnyObject,
  TQuery extends TSearchable,
> extends AdvancedSearchProps<TSearchable, TQuery> {
  isFetching?: boolean;
  total?: number;
}

/**
 * Versione del form "AdvancedSearch" che si espande e si contrae, con
 * messaggio di filtro attivo.
 * Da mettere come `title` delle `TableData`.
 */
export function AdvancedSearchCollapseForm<
  TSearchable extends AnyObject,
  TQuery extends TSearchable,
>(props: IAdvancedSearchCollapseFormProps<TSearchable, TQuery>) {
  const { query, setQuery, defaultQuery, isFetching, total } = props;
  const { t } = useTranslation();

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [isFetching, query, defaultQuery]);

  return (
    <div>
      <TableSearchCollapse filtered={isFiltered}>
        <AdvancedSearch {...props} />
      </TableSearchCollapse>
      <AdminMessageFilteredList
        style={{ marginBottom: 0 }}
        visible={isFiltered}
        items={total ?? null}
        message={t(
          'advanced-search.collapse-form-messagge',
          'risultati trovati in base ai filtri di ricerca.',
        )}
        action={
          <Button
            size="small"
            type="link"
            style={{ marginTop: '-2px' }}
            onClick={() => setQuery({ ...defaultQuery })}
            children={t('advanced-search.remove-filters', 'Rimuovi filtri')}
          />
        }
      />
    </div>
  );
}
